import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useFeedbackList, useNewFeedbackCount } from '@/api/queries';
import type { FeedbackListParams } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { useDebouncedSearch } from '@/ui/useDebouncedSearch';

import { applyPatch, feedbackSearch, parseFeedbackParams, sortPatch } from './feedbackParams';

type Update = (patch: Partial<FeedbackListParams>) => void;

function useFeedbackParams(canTriage: boolean): [FeedbackListParams, Update] {
  const [search, setSearch] = useSearchParams();
  const params = parseFeedbackParams(search, canTriage);

  function update(patch: Partial<FeedbackListParams>) {
    setSearch(feedbackSearch(applyPatch(params, patch)), { replace: true });
  }

  return [params, update];
}

/** The Feedback list's URL-backed view, its queries and the New request dialog. */
export function useFeedbackPage() {
  // Triage (everyone's requests) is the server's decision, like every screen.
  const { can } = useSession();
  const canTriage = can('feedback.view.any');
  const [params, update] = useFeedbackParams(canTriage);
  const list = useFeedbackList(params);
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useDebouncedSearch(params.q, update);
  // Requests no admin has looked at yet, counted on the All requests tab.
  const newCount = useNewFeedbackCount({ enabled: canTriage });

  function switchTab(scope: FeedbackListParams['scope']) {
    setSearch('');
    update({ scope, statuses: [], categories: [], q: '' });
  }

  return {
    canTriage,
    canSubmit: can('feedback.submit'),
    params,
    update,
    onSort: (key: string) => update(sortPatch(params, key)),
    list,
    all: params.scope === 'all',
    filtered: params.statuses.length > 0 || params.categories.length > 0 || params.q !== '',
    adding,
    setAdding,
    search,
    setSearch,
    switchTab,
    newCount,
  };
}

export type FeedbackPageState = ReturnType<typeof useFeedbackPage>;
